import { forwardRef, type Ref } from 'react'

import { HcButton } from '@hotmart-org-ca/cosmos-web/dist/react/hc-button'
import { customMedia } from '@space-core/styles'

import type { ContentProps } from '@space-shell/common/components/imageSlider/types'
import { UrlUtils } from '@space-shell/common/utils/url'
import styled from 'styled-components'

const ContentWrapperMobile = styled.div<{
  $hasMobileMedia: boolean
  $version: number
  $parentWidth: number
}>`
  position: relative;
  z-index: 10;

  ${({ $version, $parentWidth, $hasMobileMedia }) =>
    `
    ${
      $version === 0 &&
      `
      ${
        $hasMobileMedia &&
        `
        position: absolute;
        width:  ${$parentWidth}px;
        padding-bottom: 3.5rem;
        bottom: 0;
        `
      }
      `
    }
  `}
`

const ContentWrapper = styled.div`
  top: 0;
  z-index: 10;

  p {
    font-size: 3rem;
    line-height: 3.594rem;
  }

  ${customMedia.between('sm', 'lg')`
    p {
      font-size: 2rem;
      line-height: 2.594rem;
    }
  `}
`
const AnimationTextUp = styled.div<{ $isMobile?: boolean }>`
  z-index: 5;
  ${({ $isMobile }) =>
    `
    ${!$isMobile && 'margin-left: 4rem'};
    width: ${$isMobile ? '100%' : '55vw'};
    display: ${$isMobile ? 'flex' : 'block'};
  `}

  &.slideUpActive {
    animation: slideUp 0.8s ease-in-out;
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }

    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`

const Button = styled(HcButton)<{
  $hasUrl?: boolean
  $isMobile?: boolean
  $hasMobileMedia?: boolean
}>`
  ${({ $isMobile, $hasMobileMedia }) =>
    $isMobile
      ? `
    --hc-button-secondary-background-color: ${$hasMobileMedia ? 'white' : 'var(--gray-900)'};
    --hc-button-secondary-color: ${$hasMobileMedia ? '#191c1f' : 'var(--gray-100)'};
    --hc-button-secondary-border-color: transparent;
    --hc-button-secondary-hover-background-color: ${$hasMobileMedia ? 'white' : 'var(--gray-900)'};
    --hc-button-secondary-hover-color: ${$hasMobileMedia ? '#191c1f' : 'var(--gray-100)'};
    --hc-button-secondary-hover-border-color: transparent;
    --hc-button-secondary-focus-background-color: ${$hasMobileMedia ? 'white' : 'var(--gray-900)'};
    --hc-button-secondary-focus-color: ${$hasMobileMedia ? '#191c1f' : 'var(--gray-100)'};
    --hc-button-secondary-focus-border-color: transparent;
    --hc-button-secondary-active-focus-background-color: ${
      $hasMobileMedia ? 'white' : 'var(--gray-900)'
    };
    --hc-button-secondary-active-focus-color: ${$hasMobileMedia ? '#191c1f' : 'var(--gray-100)'};
    --hc-button-secondary-active-focus-border-color: transparent;
  `
      : `
    --hc-button-secondary-background-color: white;
    --hc-button-secondary-color: #191c1f;
    --hc-button-secondary-border-color: transparent;
    --hc-button-secondary-hover-background-color: white;
    --hc-button-secondary-hover-color: #191c1f;
    --hc-button-secondary-hover-border-color: transparent;
    --hc-button-secondary-focus-background-color: white;
    --hc-button-secondary-focus-color: #191c1f;
    --hc-button-secondary-focus-border-color: transparent;
    --hc-button-secondary-active-focus-background-color: white;
    --hc-button-secondary-active-focus-color: #191c1f;
    --hc-button-secondary-active-focus-border-color: transparent;
  `}

  ${({ $hasUrl }) =>
    `
    --hc-button-cursor: ${$hasUrl ? 'pointer' : 'default'};
  `}
`

const Text = styled.p<{ $hasMobileMedia: boolean }>`
  color: ${({ $hasMobileMedia }) => ($hasMobileMedia ? 'white' : 'var(--gray-900)')};
`

const Content = ({ slide, isMobile, parentWidth }: ContentProps, ref: Ref<HTMLDivElement>) => {
  const shouldShowButton = slide.buttons?.some((button) => Boolean(button?.label))
  const hasText = Boolean(slide.switchText && slide.text)
  const hasButton = slide.switchButton && shouldShowButton
  const hasContent = hasText || hasButton
  const hasUrl = slide.buttons?.some((button) => Boolean(button?.url)) && slide.switchButton
  if (!hasContent) {
    return null
  }

  const handleClickButton = () => {
    if (hasUrl) {
      window.open(UrlUtils.addSckParam(slide?.buttons?.[0].url), '_blank')
    }
  }

  if (isMobile) {
    return (
      <ContentWrapperMobile
        className='_px-4 _d-flex'
        $hasMobileMedia={Boolean(slide.imageUrlMobile) || Boolean(slide.videoUrlMobile)}
        $version={Number(slide.version)}
        $parentWidth={Number(parentWidth)}>
        <AnimationTextUp ref={ref} $isMobile className='_flex-column _align-items-center'>
          {hasText && (
            <Text
              $hasMobileMedia={Boolean(slide.imageUrlMobile) || Boolean(slide.videoUrlMobile)}
              className='_text-5 _text-wrap _font-weight-bold _line-height _text-center'>
              {slide.text}
            </Text>
          )}
          {hasButton && (
            <Button
              $isMobile={isMobile}
              $hasMobileMedia={Boolean(slide.imageUrlMobile) || Boolean(slide.videoUrlMobile)}
              variation='secondary'
              onClick={handleClickButton}>
              {slide.buttons?.[0]?.label}
            </Button>
          )}
        </AnimationTextUp>
      </ContentWrapperMobile>
    )
  }

  return (
    <ContentWrapper className='_position-absolute _flex-column _h-full _d-flex _justify-content-center _align-items-baseline'>
      <AnimationTextUp ref={ref}>
        {hasText && <p className='_text-white _text-wrap _font-weight-bold _mb-4'>{slide.text}</p>}
        {hasButton && (
          <Button $hasUrl={hasUrl} variation='secondary' onClick={handleClickButton}>
            {slide.buttons?.[0]?.label}
          </Button>
        )}
      </AnimationTextUp>
    </ContentWrapper>
  )
}

export default forwardRef(Content)
